import { useEffect, useState } from "react";

import {
	Button,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@material-ui/core";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import { GridReadyEvent } from "ag-grid-community";
import "ag-grid-enterprise";
import "ag-grid-enterprise/styles/ag-grid.css";
import "ag-grid-enterprise/styles/ag-theme-balham.min.css";
import { AgGridReact } from "ag-grid-react";

import { useRootTypeContext } from "@/pages/configurations/rootTypes/context/RootTypeContext";
import { CreateRoot } from "../CreateRoot/CreateRoot";
import { DeleteRoot } from "../DeleteRoot/DeleteRoot";
import { EditRoot } from "../EditRoot/EditRoot";
import { useTableData } from "./customHooks/useTableData";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			"& .MuiDataGrid-row": {
				cursor: "pointer",
			},
			"& .MuiDataGrid-columnHeaderTitle, & .MuiTablePagination-caption": {
				fontWeight: "bold",
				fontSize: "12px",
			},
			"& .MuiDataGrid-columnHeaderTitleContainer": {
				padding: "0",
			},
			"& .MuiDataGrid-cell--textLeft": {
				fontSize: "12px",
			},
		},
		relative: {
			position: "relative",
		},
		textfield: {
			margin: theme.spacing(2),
		},
		btnModal: {
			display: "flex",
			justifyContent: "flex-end",
		},
		dataTable: {
			padding: theme.spacing(1, 2),
		},
	}),
);

type Props = {
	isLoading: boolean;
	getRootsFromAPI: Function;
};

export const TableRoots = ({ isLoading, getRootsFromAPI }: Props) => {
	const [filterInput, setFilterInput] = useState("");
	const [rootTypeFilter, setRootTypeFilter] = useState("");

	const [modalCreateRoot, setModalCreateRoot] = useState(false);
	const [modalEditRoot, setModalEditRoot] = useState(false);
	const [modalDeleteRoot, setModalDeleteRoot] = useState(false);
	const [commentsModal, setCommentsModal] = useState(false);

	const {
		state: { rootTypes },
	} = useRootTypeContext();

	const [rootIdForEdit, setRootIdForEdit] = useState<number | null>(null);
	const [rootIdForDelete, setRootIdForDelete] = useState<number | null>(null);

	const classes = useStyles();

	const { rows, columns, defaultColDef } = useTableData({
		setRootIdForEdit,
		setModalEditRoot,
		setRootIdForDelete,
		setModalDeleteRoot,
		setCommentsModal,
	});

	const [gridAPI, setGridAPI] = useState<any>(null);

	const handleRootTypeChange = (e: any) => {
		setRootTypeFilter(e.target.value as string);
	};

	const handlerFilter = (e: any) => {
		setFilterInput(e.target.value);

		gridAPI.setQuickFilter(e.target.value);
	};

	const onGridReady = (params: GridReadyEvent) => {
		setGridAPI(params.api);
		params.api.sizeColumnsToFit();
	};

	useEffect(() => {
		if (isLoading) {
			gridAPI?.showLoadingOverlay();
		} else {
			gridAPI?.hideOverlay();
		}
	}, [isLoading, gridAPI]);

	useEffect(() => {
		if (rootTypeFilter) {
			(async () => {
				gridAPI?.showLoadingOverlay();

				const rootTypeIdFilterInstance =
					gridAPI?.getFilterInstance("rootTypeId");

				rootTypeIdFilterInstance?.setModel({
					values: [rootTypeFilter],
				});

				gridAPI?.onFilterChanged();
				gridAPI?.hideOverlay();
			})();
		} else {
			const rootTypeIdFilterInstance = gridAPI?.getFilterInstance("rootTypeId");
			rootTypeIdFilterInstance?.setModel(null);
			gridAPI?.onFilterChanged();
		}
	}, [rootTypeFilter]);

	return (
		<Grid container justifyContent="center">
			<Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
				<Typography
					variant="h5"
					style={{ margin: "20px 0 20px", fontWeight: 600 }}
				>
					Roots Management
				</Typography>
			</Grid>
			<Grid
				container
				justifyContent="center"
				alignItems="center"
				className={classes.root}
			>
				<Grid
					item
					md={12}
					xs={12}
					style={{ padding: "0 15px" }}
					className={classes.relative}
				>
					<CreateRoot
						show={modalCreateRoot}
						onHide={(shouldUpdate: boolean) => {
							setModalCreateRoot(false);
							if (shouldUpdate) getRootsFromAPI().then(() => {});
						}}
					/>
					<DeleteRoot
						rootIdForDelete={rootIdForDelete}
						show={modalDeleteRoot}
						onHide={(shouldUpdate: boolean) => {
							setModalDeleteRoot(false);
							if (shouldUpdate) getRootsFromAPI().then(() => {});
						}}
					/>
					<EditRoot
						rootIdForEdit={rootIdForEdit}
						show={modalEditRoot}
						onHide={(shouldUpdate: boolean) => {
							setModalEditRoot(false);
							if (shouldUpdate) getRootsFromAPI().then(() => {});
						}}
					/>

					<div
						className="ag-theme-balham"
						style={{ width: "100%", height: "60vh" }}
					>
						<Grid
							container
							spacing={2}
							md={12}
							xs={12}
							style={{ width: "100rem", padding: "10px 0px" }}
						>
							<Grid item xs={12} md={6}>
								<FormControl style={{ width: "100%" }} variant="outlined">
									<InputLabel id="root-type-select-label">Root Type</InputLabel>
									<Select
										style={{ width: "100%" }}
										value={rootTypeFilter}
										label="Root Type"
										onChange={handleRootTypeChange}
										disabled={isLoading}
									>
										<MenuItem value={""}>All</MenuItem>
										{!isLoading &&
											rootTypes?.map((rt) => {
												return (
													<MenuItem
														value={rt?.rootTypeName}
														key={rt?.rootTypeId}
													>
														{rt?.rootTypeName}
													</MenuItem>
												);
											})}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									id="outlined-basic"
									label="Filter..."
									variant="outlined"
									style={{ width: "100%" }}
									value={filterInput}
									onChange={handlerFilter}
								/>
							</Grid>
						</Grid>

						<AgGridReact
							onGridReady={onGridReady}
							rowData={rows}
							columnDefs={columns}
							defaultColDef={defaultColDef}
						/>
					</div>
					<Grid container justifyContent="flex-end">
						<Grid item md={2} xs={12} className={classes.btnModal}>
							<Button
								variant="contained"
								color="primary"
								onClick={() => setModalCreateRoot(!modalCreateRoot)}
								fullWidth
								style={{ margin: "100px 0px 0px" }}
							>
								New root
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
